import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Search,
  RefreshCcw,
  Filter,
  Mail,
  Phone,
  Building2,
  Clock,
  Trash2,
  Sparkles,
} from "lucide-react";
import { STATUS_OPTIONS, getStatusMeta } from "@/lib/statuses";
import { QualifyDialog } from "@/components/admin/QualifyDialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const INDUSTRY_OPTIONS = [
  "Food Manufacturing",
  "Hospitality / F&B",
  "Distribution & Warehousing",
  "Import / Export",
  "Other SME (Scaling Operations)",
];

const inputCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none focus:ring-0";

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, follow_up: 0, converted: 0, not_interested: 0, by_industry: [], by_source: [] });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [team, setTeam] = useState([]);
  const [qualifyTarget, setQualifyTarget] = useState(null);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (industryFilter) params.industry = industryFilter;
      if (q.trim()) params.q = q.trim();
      const [listRes, statsRes, teamRes] = await Promise.all([
        api.get("/admin/enquiries", { params }),
        api.get("/admin/enquiries/stats"),
        api.get("/admin/team").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(listRes.data.items || []);
      setStats(statsRes.data);
      setTeam(teamRes.data.items || []);
    } catch (e) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, industryFilter, q]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setStatus = async (it, next) => {
    if (next === it.status) return;
    try {
      await api.patch(`/admin/enquiries/${it.id}`, { status: next });
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, status: next } : x)));
      setSelected((s) => (s && s.id === it.id ? { ...s, status: next } : s));
      const meta = getStatusMeta(next);
      toast.success(`Status updated to ${meta.label}`);
      const s = await api.get("/admin/enquiries/stats");
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const removeEnquiry = async (it) => {
    setEnquiryToDelete(it);
  };

  const confirmDeleteEnquiry = async () => {
    if (!enquiryToDelete) return;
    try {
      await api.delete(`/admin/enquiries/${enquiryToDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== enquiryToDelete.id));
      if (selected?.id === enquiryToDelete.id) setSelected(null);
      const s = await api.get("/admin/enquiries/stats");
      setStats(s.data);
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setEnquiryToDelete(null);
    }
  };

  const exportCSV = () => {
    const headers = [
      "id","status","created_at","name","work_email","phone","company","industry","company_size","compliance_stage","challenge",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [headers.join(",")].concat(
      items.map((it) => headers.map((h) => escape(it[h])).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vanguard-beacon-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredCount = useMemo(() => items.length, [items]);

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard-page">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">Lead inbox</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-dashboard-heading">
              Enquiries
            </h1>
            <p className="mt-2 text-sm text-brand-slate/75">
              All operational review enquiries submitted from the landing page.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-white rounded-full transition-colors"
              data-testid="admin-refresh-button"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              onClick={exportCSV}
              disabled={items.length === 0}
              className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-slate rounded-full transition-colors disabled:opacity-40"
              data-testid="admin-export-button"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} testid="stat-total" />
          <StatCard label="New" value={stats.new} accent testid="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} testid="stat-contacted" />
          <StatCard label="Follow up" value={stats.follow_up} testid="stat-follow-up" />
          <StatCard label="Converted" value={stats.converted} testid="stat-converted" />
          <StatCard label="Not interested" value={stats.not_interested} testid="stat-not-interested" />
        </div>

        {/* Where deals come from */}
        {(stats.by_source?.length || 0) > 0 && (
          <section className="bg-white border border-brand-navy/10 p-5 md:p-6 mb-8 rounded-2xl" data-testid="source-breakdown-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-brand-orange font-semibold">Attribution</p>
                <h3 className="text-base font-semibold text-brand-navy">Where deals come from</h3>
              </div>
              <span className="text-[11px] text-brand-slate/60">Based on utm_source (fallback = direct)</span>
            </div>
            <ul className="space-y-2">
              {stats.by_source.map((row) => {
                const pct = stats.total ? Math.round((row.n / stats.total) * 100) : 0;
                return (
                  <li key={row.source} className="flex items-center gap-3" data-testid={`source-row-${row.source}`}>
                    <span className="w-32 shrink-0 text-xs uppercase tracking-wider text-brand-navy/70">{row.source}</span>
                    <div className="flex-1 h-2 bg-brand-cream rounded-full overflow-hidden">
                      <div className="h-full bg-brand-orange" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-14 text-right text-xs text-brand-slate/70">{row.n} · {pct}%</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Filters */}
        <div className="bg-white border border-brand-navy/10 p-4 md:p-5 mb-6 grid md:grid-cols-12 gap-3 rounded-2xl">
          <div className="md:col-span-5 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate/50" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, company, or email…"
              className="pl-9 rounded-xl h-11"
              data-testid="admin-search-input"
            />
          </div>
          <div className="md:col-span-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={inputCls}
              data-testid="admin-filter-status"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className={inputCls}
              data-testid="admin-filter-industry"
            >
              <option value="">All industries</option>
              {INDUSTRY_OPTIONS.map((i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 flex items-center justify-end text-xs text-brand-slate/60">
            <Filter size={14} className="mr-1" /> {filteredCount}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-brand-navy/10 overflow-hidden rounded-2xl" data-testid="admin-enquiries-table-wrap">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-brand-cream/60">
                  <TableHead className="text-brand-navy">Name</TableHead>
                  <TableHead className="text-brand-navy">Company</TableHead>
                  <TableHead className="text-brand-navy hidden md:table-cell">Industry</TableHead>
                  <TableHead className="text-brand-navy hidden lg:table-cell">Stage</TableHead>
                  <TableHead className="text-brand-navy">Status</TableHead>
                  <TableHead className="text-brand-navy hidden md:table-cell">Received</TableHead>
                  <TableHead className="text-brand-navy text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-brand-slate/60">Loading…</TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-brand-slate/60" data-testid="admin-empty-state">
                      No enquiries yet. They'll appear here as soon as visitors submit the form.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.map((it) => {
                  const meta = getStatusMeta(it.status);
                  return (
                    <TableRow
                      key={it.id}
                      className="cursor-pointer hover:bg-brand-cream/40"
                      onClick={() => setSelected(it)}
                      data-testid={`admin-row-${it.id}`}
                    >
                      <TableCell className="font-medium text-brand-navy">{it.name}</TableCell>
                      <TableCell className="text-brand-slate">
                        <div className="flex items-center gap-2"><Building2 size={14} className="text-brand-slate/50" />{it.company}</div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-brand-slate text-sm">{it.industry}</TableCell>
                      <TableCell className="hidden lg:table-cell text-brand-slate text-sm">{it.compliance_stage}</TableCell>
                      <TableCell>
                        <Badge className={`${meta.className} rounded-full`}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-brand-slate/70 text-sm">
                        {formatDate(it.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end flex-wrap" onClick={(e) => e.stopPropagation()}>
                          {!it.converted_deal_id && (
                            <button
                              onClick={() => setQualifyTarget(it)}
                              className="inline-flex items-center gap-1 bg-brand-orange/10 text-brand-orange hover:bg-brand-orange hover:text-white border border-brand-orange/30 rounded-full px-3 py-1 text-[11px] font-semibold transition-colors"
                              data-testid={`admin-qualify-${it.id}`}
                            >
                              <Sparkles size={12} /> Qualify
                            </button>
                          )}
                          {it.converted_deal_id && (
                            <span className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold" data-testid={`admin-converted-badge-${it.id}`}>
                              ✓ In pipeline
                            </span>
                          )}
                          <select
                            value={it.status}
                            onChange={(e) => setStatus(it, e.target.value)}
                            className="rounded-full text-xs font-medium border border-brand-navy/15 bg-white text-brand-navy px-3 py-1.5 focus:border-brand-orange focus:outline-none"
                            data-testid={`admin-status-select-${it.id}`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => removeEnquiry(it)}
                            className="text-brand-slate/60 hover:text-red-600 p-1"
                            aria-label="Delete"
                            data-testid={`admin-delete-${it.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Detail sheet */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="admin-detail-sheet">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-brand-navy">{selected.name}</SheetTitle>
                  <SheetDescription>{selected.company} · {selected.industry}</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4 text-sm">
                  <DetailRow icon={Mail} label="Email">
                    <a className="text-brand-orange" href={`mailto:${selected.work_email}`}>{selected.work_email}</a>
                  </DetailRow>
                  <DetailRow icon={Phone} label="Phone">
                    <a className="text-brand-orange" href={`tel:${selected.phone}`}>{selected.phone}</a>
                  </DetailRow>
                  <DetailRow icon={Building2} label="Company size">
                    {selected.company_size}
                  </DetailRow>
                  <DetailRow icon={Clock} label="Compliance stage">
                    {selected.compliance_stage}
                  </DetailRow>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-brand-slate/60 mb-1">Biggest challenge</p>
                    <p className="text-brand-slate leading-relaxed whitespace-pre-wrap">
                      {selected.challenge?.trim() ? selected.challenge : <span className="text-brand-slate/50">— Not provided</span>}
                    </p>
                  </div>
                  <div className="pt-2 border-t border-brand-navy/10">
                    <p className="text-xs text-brand-slate/60">Received {formatDate(selected.created_at)}</p>
                    {selected.user_agent && <p className="text-xs text-brand-slate/40 mt-1 break-words">{selected.user_agent}</p>}
                  </div>

                  <div className="pt-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-brand-slate/60 mb-2">Update status</p>
                    <select
                      value={selected.status}
                      onChange={(e) => setStatus(selected, e.target.value)}
                      className={inputCls}
                      data-testid="admin-detail-status-select"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="pt-4 flex flex-wrap gap-3">
                    <a
                      href={`mailto:${selected.work_email}?subject=Your Vanguard Beacon Operational Review`}
                      className="bg-brand-navy text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-slate rounded-full"
                      data-testid="admin-detail-email"
                    >
                      Reply by email
                    </a>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </main>

      <QualifyDialog
        open={!!qualifyTarget}
        onOpenChange={(o) => !o && setQualifyTarget(null)}
        enquiry={qualifyTarget}
        team={team}
        onConverted={({ deal }) => {
          setItems((arr) =>
            arr.map((x) =>
              x.id === qualifyTarget.id
                ? { ...x, status: "converted", converted_deal_id: deal.id }
                : x
            )
          );
          if (selected?.id === qualifyTarget.id) {
            setSelected((s) => ({ ...s, status: "converted", converted_deal_id: deal.id }));
          }
          api.get("/admin/enquiries/stats").then((s) => setStats(s.data));
        }}
      />

      <ConfirmDialog
        open={!!enquiryToDelete}
        onOpenChange={(o) => !o && setEnquiryToDelete(null)}
        title="Delete this enquiry?"
        description={enquiryToDelete ? `Enquiry from ${enquiryToDelete.name} (${enquiryToDelete.company}) will be permanently removed.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDeleteEnquiry}
      />
    </div>
  );
}

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-brand-slate/50"><Icon size={16} /></span>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-brand-slate/60">{label}</p>
        <div className="text-brand-navy">{children}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className={`p-5 border bg-white rounded-2xl ${accent ? "border-brand-orange/40" : "border-brand-navy/10"}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-brand-slate/65">{label}</p>
      <p className={`mt-2 text-3xl font-semibold ${accent ? "text-brand-orange" : "text-brand-navy"}`}>{value ?? 0}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date} ${time}`;
}
